import React from "react";

import { LeafIcon as Leaf, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import Dialog from "ds/Dialog";
import { formatAsCurrency } from "Libs/utils";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";

import * as S from "./InvoiceSummary.styles";

import type { Order, LineItemComponent } from "@packages/client";

type InvoiceSummaryProps = {
  order: Order;
  orderCurrency: string;
};

const InvoiceSummary = ({ order, orderCurrency }: InvoiceSummaryProps) => {
  const intl = useIntl();
  const componentsValues = Object.values(order.components).sort(
    (a, b) => b.amount - a.amount
  );

  const shouldShowEstimation = order.status === "recurring_open";

  const shouldShowGreenIcon = (item: LineItemComponent) => {
    return item.display_title.toLowerCase().includes("green discount");
  };

  return (
    <S.InvoiceSummaryWrapper>
      {componentsValues.map(item => {
        const greenIcon = shouldShowGreenIcon(item);
        return (
          <React.Fragment key={item.display_title}>
            <S.InvoiceSummaryRow>
              <S.InvoiceName>
                <span>{item.display_title}</span>
                {greenIcon && (
                  <Dialog
                    analyticId="green_discount.description"
                    trigger={
                      <O.IconWrapper aria-label="leaf">
                        <Leaf color="var(--theme-success-600)" />
                      </O.IconWrapper>
                    }
                    title={intl.formatMessage({
                      id: "green_discount.title"
                    })}
                    description={intl.formatMessage({
                      id: "green_discount.description"
                    })}
                  />
                )}
              </S.InvoiceName>
              {greenIcon && (
                <Typography.BodyText size="medium" weight="regular">
                  {intl.formatMessage({
                    id: "billing.invoice.project_green_discount_description",
                    defaultMessage: "3% of CPU, memory, and build resources"
                  })}
                </Typography.BodyText>
              )}
              <S.InvoiceSummarySum>
                {formatAsCurrency(item.amount, orderCurrency)}
              </S.InvoiceSummarySum>
            </S.InvoiceSummaryRow>
          </React.Fragment>
        );
      })}
      <S.InvoiceTotalRow>
        <div>
          <S.Total>
            {intl.formatMessage({
              id: shouldShowEstimation
                ? "billing.invoice.estimated_total"
                : `billing.invoice.total`
            })}
          </S.Total>
        </div>
        <S.InvoiceSummarySum>
          {formatAsCurrency(order.total, orderCurrency)}
        </S.InvoiceSummarySum>
      </S.InvoiceTotalRow>
    </S.InvoiceSummaryWrapper>
  );
};

export default InvoiceSummary;
